const NODE_HEIGHT = 130
/** Build an SVG path through waypoints with rounded corners (quadratic bezier at each turn). */
export const buildRoundedPath = (pts: { x: number; y: number }[], r: number): string => {
  if (pts.length < 2) return ''
  let d = `M ${pts[0]!.x} ${pts[0]!.y}`
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i - 1]!,
      c = pts[i]!,
      n = pts[i + 1]!
    const dx1 = c.x - p.x,
      dy1 = c.y - p.y
    const dx2 = n.x - c.x,
      dy2 = n.y - c.y
    const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
    const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)
    if (l1 < 1 || l2 < 1) {
      d += ` L ${c.x} ${c.y}`
      continue
    }
    const cr = Math.min(r, l1 / 2, l2 / 2)
    const bx = c.x - (dx1 / l1) * cr
    const by = c.y - (dy1 / l1) * cr
    const cx2 = c.x + (dx2 / l2) * cr
    const cy2 = c.y + (dy2 / l2) * cr
    d += ` L ${bx} ${by} Q ${c.x} ${c.y} ${cx2} ${cy2}`
  }
  d += ` L ${pts[pts.length - 1]!.x} ${pts[pts.length - 1]!.y}`
  return d
}

/** Compute edge path between two port points.
 *  - Nearly horizontal forward → straight line
 *  - Forward/close → bezier S-curve
 *  - Truly backward (dx < -EXIT_PAD*2) → orthogonal U-routing with rounded corners */
const EXIT_PAD = 60
export const smartBezier = (a: { x: number; y: number }, b: { x: number; y: number }): string => {
  const dx = b.x - a.x
  const dy = b.y - a.y

  // Nearly horizontal forward → straight line
  if (dx > 20 && Math.abs(dy) < 50) {
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`
  }

  // Forward/close range: bezier S-curve.
  // Also handles small negative dx (slightly backward) where orthogonal would loop.
  if (dx > -(EXIT_PAD * 2)) {
    const offset = Math.max(70, Math.abs(dx) * 0.4 + Math.abs(dy) * 0.25)
    return `M ${a.x} ${a.y} C ${a.x + offset} ${a.y}, ${b.x - offset} ${b.y}, ${b.x} ${b.y}`
  }

  // Truly backward → orthogonal U-routing: exit right → lane → enter left
  const goBelow = b.y >= a.y - 20
  const routeY = goBelow
    ? Math.max(a.y, b.y) + NODE_HEIGHT * 0.65
    : Math.min(a.y, b.y) - NODE_HEIGHT * 0.65

  return buildRoundedPath(
    [
      a,
      { x: a.x + EXIT_PAD, y: a.y },
      { x: a.x + EXIT_PAD, y: routeY },
      { x: b.x - EXIT_PAD, y: routeY },
      { x: b.x - EXIT_PAD, y: b.y },
      b,
    ],
    14,
  )
}
